import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Card } from '../models/Card';
import { Deck, SerializableDeck } from '../models/Deck';
import { DeckConfig } from '../models/DeckConfig';
import { PendingObject } from '../models/PendingObject';
import { StackCard } from '../models/StackCard';
import { WindowSize } from '../models/WindowSize';
import DeckValidator from '../services/DeckValidator';
import { StorageHelper, useStorageWatcher } from '../services/Storage';
import BuilderHeader from './BuilderHeader';
import DropArea from './DropArea';
import FavoriteCards from './FavoriteCards';
import NormalCards from './NormalCards';
import ValidationWarningModal, { useValidationModal } from './ValidationWarningModal';
import { WebStorageAlert } from './WebStorageAlert';

export default function CardsRoot(props: { windowSize: WindowSize, toScaleUp: () => any, toScaleDown: () => any }) {
    const [isDragging, setIsDragging] = useState(false);

    const handleCardDragStart = useCallback(() => {
        setIsDragging(true);
    }, []);

    const handleCardDragEnd = useCallback(() => {
        setIsDragging(false);
    }, []);

    //Register drag events.
    useEffect(() => {
        window.addEventListener('carddragstart', handleCardDragStart);
        window.addEventListener('carddragend', handleCardDragEnd);

        return () => {
            window.removeEventListener('carddragstart', handleCardDragStart);
            window.removeEventListener('carddragend', handleCardDragEnd);
        };
    }, [handleCardDragStart, handleCardDragEnd]);


    const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        if (event.dataTransfer && [...event.dataTransfer.types].includes('application/json')) {
            event.preventDefault();
        }
    }, []);

    //デッキデータ
    const [deck, queuedCard, deckOperator] = useDeck();

    //検証警告モーダル
    const [isConfirmWarningModalOpen, errors, pendingObject, confirmWarningModalOperation] = useValidationModal();
    const handleWarningContinue = useCallback((pendingObject: PendingObject) => {
        if (pendingObject.operation === 'addNormalCard') {
            confirmWarningModalOperation.close();
            deckOperator.addNormalCard(pendingObject.card);

        } else if (pendingObject.operation === 'changeCardCount' && pendingObject.value !== undefined) {
            confirmWarningModalOperation.close();
            deckOperator.changeCardCount(pendingObject.card, pendingObject.value);

        } else if (pendingObject.operation === 'markFavoriteCard') {
            confirmWarningModalOperation.close();
            deckOperator.markFavoriteCard(pendingObject.card);

        }
    }, [deckOperator, confirmWarningModalOperation]);

    //ドロップ処理
    const insertCardWithValidate = useCallback((card: Card) => {
        //追加時の検証・警告メッセージの表示を行う
        const errors = DeckValidator.validateInseringCard(deck, card);
        if (errors && errors.length >= 1) {
            //警告メッセージを表示
            confirmWarningModalOperation.setErrors(errors);
            confirmWarningModalOperation.setPendingObject({ card: card, operation: "addNormalCard" });
            confirmWarningModalOperation.open();
        } else {
            deckOperator.addNormalCard(card);
        }
    }, [deck, deckOperator, confirmWarningModalOperation]);

    const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        if (event.dataTransfer) {
            const binded = event.dataTransfer.getData('application/json');

            try {
                if (!binded) {
                    throw Error('data type not included.');
                }

                const card = Card.parseCard(binded);
                insertCardWithValidate(card);

            } catch (error) {
                //ignore parse error
                console.warn('dropped item is ignored.', error);
            }
        }
    }, [insertCardWithValidate]);

    const handleChangeCardCount = useCallback((stackCard: StackCard, newValue: number) => {

        //追加時の検証・警告メッセージの表示を行う(減算時は無視する)
        const errors = DeckValidator.validateInseringCard(deck, stackCard.card);
        if (newValue >= stackCard.count && errors && errors.length >= 1) {
            //警告メッセージを表示
            confirmWarningModalOperation.setErrors(errors);
            confirmWarningModalOperation.setPendingObject({ card: stackCard.card, operation: "changeCardCount", value: newValue });
            confirmWarningModalOperation.open();
        } else {
            deckOperator.changeCardCount(stackCard.card, newValue);
        }

    }, [deck, deckOperator, confirmWarningModalOperation]);

    const handleMarkFavoriteCard = useCallback((card: Card) => {

        const errors = DeckValidator.validateMarkFavorite(deck, card);
        if (errors && errors.length >= 1) {
            //警告メッセージを表示
            confirmWarningModalOperation.setErrors(errors);
            confirmWarningModalOperation.setPendingObject({ card: card, operation: "markFavoriteCard" });
            confirmWarningModalOperation.open();
        } else {
            deckOperator.markFavoriteCard(card);
        }


    }, [deck, deckOperator, confirmWarningModalOperation]);

    const handleInsertEvent = useCallback((event: CustomEvent) => {
        try {
            const value = event.detail;
            if (value
                && value.cardNo
                && value.cardName
                && Number.isInteger(value.cardProductId)) {
                insertCardWithValidate(new Card(value.cardProductId, value.cardNo, value.cardName, value.canFavorite === true));
            }

        } catch (error) {
            console.warn('insert event ignored.', error);
        }
    }, [insertCardWithValidate]);

    //Register custom add event.
    useEffect(() => {
        window.addEventListener('insertcard', handleInsertEvent as EventListenerOrEventListenerObject);

        return () => {
            window.removeEventListener('insertcard', handleInsertEvent as EventListenerOrEventListenerObject);
        };
    }, [handleInsertEvent]);

    const scrollBoxRef = useRef<HTMLDivElement>(null);
    useLayoutEffect(() => {
        if (queuedCard && queuedCard.current) {
            const element = scrollBoxRef?.current?.querySelector(`[data-uuid='${queuedCard.current.uuid}']`);
            element?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
            queuedCard.current = null;
        }
    });

    const [visibleCardOverlay, setVisibleCardOverlay] = useState<Card | null>(null);
    const handleCardClicked = useCallback((card?: Card) => {
        if (!card) {
            setVisibleCardOverlay(null);
        } else {
            setVisibleCardOverlay(card);
        }

    }, [visibleCardOverlay])

    return <>
        <BuilderHeader deck={deck} onClearAllCards={deckOperator.clearCards} windowSize={props.windowSize} toScaleDown={props.toScaleDown} toScaleUp={props.toScaleUp} onDeckLoaded={deckOperator.loadFromSerializableDeck} />
        <WebStorageAlert />
        <div className='position-relative'>
            <div className='card-root-scroll-box'>
                <div className='card-root-container' onDragOver={handleDragOver} onDrop={handleDrop}>
                    <div className='cards' ref={scrollBoxRef}>
                        <FavoriteCards deck={deck} selectedCard={visibleCardOverlay} removeFavoriteCard={deckOperator.removeFavoriteCard} unmarkFavoriteCard={deckOperator.unmarkFavoriteCard} onClicked={handleCardClicked} />
                        <NormalCards deck={deck} selectedCard={visibleCardOverlay} removeNormalCard={deckOperator.removeNormalCard} markFavoriteCard={handleMarkFavoriteCard} onCardCountChanged={handleChangeCardCount} onClicked={handleCardClicked} />
                    </div>
                </div>
            </div>
            {
                isDragging ? <DropArea /> : null
            }
        </div>
        <ValidationWarningModal isOpen={isConfirmWarningModalOpen} errors={errors} pendingObject={pendingObject} onCancel={confirmWarningModalOperation.close} onContinue={handleWarningContinue} />
    </>;
};

type useDeckFunctions = [
    Deck,
    React.MutableRefObject<Card | null>,
    {
        addNormalCard: (card: Card) => void,
        removeNormalCard: (card: Card) => void,
        markFavoriteCard: (card: Card) => void,
        unmarkFavoriteCard: (card: Card) => void,
        removeFavoriteCard: (card: Card) => void,
        changeCardCount: (card: Card, newValue: number) => void,
        clearCards: () => void
        loadFromSerializableDeck: (deck: SerializableDeck) => void
    }
];

function useDeck(): useDeckFunctions {
    const [deck, setDeck] = useState<Deck>(new Deck());
    const queuedCard = useRef<Card | null>(null);

    const isInitialLoaded = useRef(false);
    const isAutoSaveEnabled = useRef(false);
    const localDeckName = "local-deck1";


    const forceReloadDeck = useCallback(() => {
        if (isInitialLoaded.current) {
            const storageDeck = StorageHelper.getDeck(localDeckName);
            if (storageDeck) {
                setDeck(storageDeck);
                //console.log("Deck force reloaded.", storageDeck);
            }
        }
    }, [isInitialLoaded]);

    //変更検出
    /*const watcher = */useStorageWatcher(localDeckName, forceReloadDeck);

    const addNormalCard = useCallback((card: Card) => {
        const newDecks = deck.normalCards;
        const hasFavoriteCard = deck.favoriteCards.findIndex(item => item.isEqual(card)) >= 0;

        Deck.insertStackCardOrdered(newDecks, card, hasFavoriteCard ? 1 : DeckConfig.CountOfIfNewStack);

        setDeck(new Deck(deck.favoriteCards, newDecks));
        queuedCard.current = card;
    }, [deck]);

    const removeNormalCard = useCallback((card: Card) => {
        const newDecks = deck.normalCards;

        const foundIndex = newDecks.findIndex((element) => element.card.isEqual(card));
        if (foundIndex >= 0) {
            newDecks.splice(foundIndex, 1);

            setDeck(new Deck(deck.favoriteCards, newDecks));
        }
    }, [deck]);

    const markFavoriteCard = useCallback((card: Card) => {
        const newNormalDecks = deck.normalCards;
        const newFavoriteDecks = deck.favoriteCards;

        const foundIndex = newNormalDecks.findIndex((element) => element.card.isEqual(card));
        if (foundIndex >= 0) {
            const stackCard = newNormalDecks[foundIndex];
            const newCardInstance = new Card(card.cardProductId, card.cardNo, card.cardName, card.canFavorite);
            if (stackCard.count <= 1) {
                newNormalDecks.splice(foundIndex, 1);
            } else {
                stackCard.count--;
            }
            Deck.insertCardOrdered(newFavoriteDecks, newCardInstance);

            setDeck(new Deck(newFavoriteDecks, newNormalDecks));
            queuedCard.current = newCardInstance;
        }
    }, [deck]);

    const unmarkFavoriteCard = useCallback((card: Card) => {
        const newNormalDecks = deck.normalCards;
        const newFavoriteDecks = deck.favoriteCards;

        const foundFavoriteCardIndex = newFavoriteDecks.findIndex((element) => element.isEqual(card));
        if (foundFavoriteCardIndex < 0) {
            //Not found favorite card.
            return;
        }

        //Remove favorite card.
        newFavoriteDecks.splice(foundFavoriteCardIndex, 1);

        const foundNormalCardIndex = newNormalDecks.findIndex((element) => element.card.isEqual(card));
        if (foundNormalCardIndex >= 0) {
            //Included normal card.
            //Increment stack card.
            newNormalDecks[foundNormalCardIndex].count++;
            queuedCard.current = newNormalDecks[foundNormalCardIndex].card;

        } else {
            //Not found normal card.
            //Insert new normal card.
            Deck.insertStackCardOrdered(newNormalDecks, card, 1);
            queuedCard.current = card;

        }

        setDeck(new Deck(newFavoriteDecks, newNormalDecks));

    }, [deck]);

    const removeFavoriteCard = useCallback((card: Card) => {
        const newDecks = deck.favoriteCards;

        const foundIndex = newDecks.findIndex((element) => element.isEqual(card));
        if (foundIndex >= 0) {
            newDecks.splice(foundIndex, 1);

            setDeck(new Deck(newDecks, deck.normalCards));
        }
    }, [deck]);

    const changeCardCount = useCallback((card: Card, newValue: number) => {
        const newDecks = deck.normalCards;

        const foundStackCard = newDecks.find((element) => element.card.isEqual(card));
        if (foundStackCard) {
            foundStackCard.count = Math.max(newValue, 1);
            setDeck(new Deck(deck.favoriteCards, newDecks));
        }

    }, [deck]);

    const clearCards = useCallback(() => {
        setDeck(new Deck());
    }, []);

    const loadFromSerializableDeck = useCallback((deck: SerializableDeck) => {
        setDeck(Deck.loadFromSerializableDeck(deck));
    }, []);

    useEffect(() => {
        if (isAutoSaveEnabled.current) {
            //Save deck to Local Storage
            //console.log("Saving deck...", localDeckName, deck);
            StorageHelper.saveDeck(localDeckName, deck);
        }
    }, [deck]);

    useEffect(() => {
        if (isInitialLoaded.current) {
            return;
        }
        isInitialLoaded.current = true;
        //Load saved deck.
        //console.log("Loading deck...");
        const storageDeck = StorageHelper.getDeck(localDeckName);
        if (storageDeck) {
            setDeck(storageDeck);
            //console.log("Deck loaded.", storageDeck);
        }
        isAutoSaveEnabled.current = true;
    }, [deck]);

    return [deck, queuedCard, { addNormalCard, removeNormalCard, markFavoriteCard, unmarkFavoriteCard, removeFavoriteCard, changeCardCount, clearCards, loadFromSerializableDeck }];
}